import { useMemo } from "react";

import { keepPreviousData } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import numeral from "numeral";

import type {
    ActivationContractBenchmarkTrafficShareData,
    BenchmarkTrafficChannelsOverviewDetailData,
} from "@similarweb/pro-api-client";
import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import { useApiQuery } from "app/modules/api-client";
import DurationService from "app/services/DurationService";

import type { BenchmarkUrlParams } from "../../types/paramsTypes";

type TrafficSource = keyof ActivationContractBenchmarkTrafficShareData;

const transformData = (channels: ActivationContractBenchmarkTrafficShareData): Partial<Record<TrafficSource, number>> =>
    Object.fromEntries(
        Object.entries(channels).map(([key, value]) => [
            key,
            Number(
                numeral(value ?? 0)
                    .multiply(100)
                    .format("0.00"),
            ),
        ]),
    );

const select = (data: BenchmarkTrafficChannelsOverviewDetailData) => ({
    competitorsAverageData: transformData(data.competitorsAverageData),
    primaryData: transformData(data.primaryData),
});

export const useChannelsOverviewData = () => {
    const { params } = useCurrentStateAndParams<BenchmarkUrlParams>();
    const { key: benchmarkId, granularity, duration } = params;

    const { enabled, startDate } = useMemo(
        () => ({
            enabled: !!swSettings.allowedDuration(duration),
            startDate: DurationService.getDurationData(duration).forAPI.iso.from,
        }),
        [duration],
    );

    return useApiQuery(
        {
            tag: "BenchmarkTracker",
            methodName: "benchmarkTrafficChannelsOverviewDetail",
            options: {
                enabled,
                placeholderData: keepPreviousData,
                select,
                throwOnError: (e) => {
                    Sentry.errorTracker({
                        e: e,
                        message: "Failed to load benchmark channels data",
                        level: "error",
                        params: {
                            extra: {
                                benchmarkId,
                                duration,
                                granularity,
                            },
                        },
                    });

                    return true;
                },
            },
        },
        {
            benchmarkId,
            granularity,
            startDate,
        },
    );
};
